import { Dialog, DialogBackdrop, DialogPanel, Transition, TransitionChild } from '@headlessui/react';
import clsx from 'clsx';
import parse from 'html-react-parser';
import { Fragment, useState } from 'react';

import { Button as EditorButton } from '~components/generic/editor/Button/Button';
import { FormElementTextarea } from '~components/generic/editor/FormElementTextarea';
import { PaperPlaneIcon } from '~components/icons';
import { useNewsletter } from '~extension/changelog/hooks/useNewsletter';
import useLabels from '~hooks/useLabels';
import styles from './NewsletterDialog.module.css';

export function NewsletterDialog() {
  const { hlNewsletterDialog, msgNewsletterDialog, btnNewsletterDialogSend, btnNewsletterDialogCancel } = useLabels([
    'hlNewsletterDialog',
    'msgNewsletterDialog',
    'btnNewsletterDialogSend',
    'btnNewsletterDialogCancel',
  ]);
  const { isNewsletterDialogOpen, closeNewsletterDialog, sendNewsletter } = useNewsletter();
  const [message, setMessage] = useState<string>(null);
  const [isSending, setIsSending] = useState(false);

  function handleSend() {
    setIsSending(true);
    void sendNewsletter(message)
      .then(success => {
        setIsSending(false);
        if (success) {
          setMessage(null);
          closeNewsletterDialog();
        }
      })
      .catch();
  }

  return (
    <Transition appear show={isNewsletterDialogOpen} as={Fragment}>
      <Dialog onClose={closeNewsletterDialog} className="newsletter-dialog">
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="min-h-screen px-4 text-center">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <DialogBackdrop className={clsx(styles.overlay, 'newsletter-dialog--overlay', 'fixed inset-0 z-50')} />
            </TransitionChild>

            {/* This element is to trick the browser into centering the modal contents. */}
            <span className="inline-block h-screen align-middle" aria-hidden="true">
              &#8203;
            </span>

            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              {isSending ? (
                <div className="inline-block w-32 max-w-md h-32 p-8 align-middle transition-all transform bg-white shadow-xl rounded">
                  <div className="absolute inset-0 flex items-center justify-center cursor-default pointer-events-none">
                    <div className="inline-block w-16 h-16 border-2 rounded-full border-gray border-solid" />
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center cursor-default pointer-events-none">
                    <div
                      className="animate-spin inline-block w-16 h-16 border-2 rounded-full border-edit border-solid"
                      style={{ borderTopColor: 'transparent' }}
                      role="status"
                    />
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center cursor-default pointer-events-none">
                    <div className="animate-bounce inline-block w-8 h-8 mt-2">
                      <PaperPlaneIcon />
                    </div>
                  </div>
                </div>
              ) : (
                <DialogPanel
                  className={clsx(
                    'newsletter-dialog--panel',
                    'inline-block w-full max-w-md p-4 my-4 bg-white overflow-hidden text-left align-middle transition-all transform shadow-xl rounded relative z-50',
                  )}
                >
                  <Dialog.Title as="h3" className={clsx('newsletter-dialog--title', 'mt-0')}>
                    {hlNewsletterDialog}
                  </Dialog.Title>

                  <Dialog.Description as="div" className={clsx('newsletter-dialog--description', 'leading-normal')}>
                    {msgNewsletterDialog}
                  </Dialog.Description>

                  <div className={clsx('newsletter-dialog--content', 'mt-4')}>
                    {isSending ? parse(message || '') : <FormElementTextarea value={message} onChange={setMessage} />}
                  </div>

                  <div className={clsx('newsletter-dialog--actions', 'mt-4 flex')}>
                    <EditorButton
                      label={btnNewsletterDialogSend}
                      icon={<PaperPlaneIcon />}
                      onClick={handleSend}
                      processing={isSending}
                    />
                    <div className="mx-2" />
                    <EditorButton
                      label={btnNewsletterDialogCancel}
                      onClick={closeNewsletterDialog}
                      disabled={isSending}
                    />
                  </div>
                </DialogPanel>
              )}
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
